/**
 * RFQ Builder (Solicitud de Cotización) — create & edit
 *
 * Route-driven: `/solicitudes-cotizacion/nueva` creates, `/:id/editar` edits.
 * Selects a project, then captures line items {alcance, description, quantity,
 * unit, is_inferred, note}. Rows can be added, removed and reordered, and are
 * grouped visually by `alcance`.
 *
 * Captura textual (#356 / #380): `description` is stored verbatim — jerga and
 * brand names are NOT corrected. A line with no quantity is flagged
 * `is_inferred` so it is never silently dropped.
 *
 * @module pages/rfq/RfqBuilder
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap';
import {
  createRfq,
  getRfq,
  updateRfq,
  replaceRfqItems,
} from '../../services/rfqService';
import { listProjects } from '../../services/projectService';
import { listClients } from '../../services/clientService';
import { toast } from '../../utils/toast';
import DashboardLayout from '../../components/layout/DashboardLayout';

const blankLine = () => ({
  __key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  alcance: '',
  name: '',
  description: '',
  quantity: '',
  unit: '',
  is_inferred: false,
  note: '',
});

const RfqBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [header, setHeader] = useState({
    project_id: '',
    client_id: '',
    title: '',
    notes: '',
  });
  const [lines, setLines] = useState([blankLine()]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Projects for the selector
  const { data: projectsResponse } = useQuery({
    queryKey: ['rfq-builder-projects'],
    queryFn: () => listProjects({ pageSize: 500 }),
    staleTime: 1000 * 60 * 5,
  });
  const projects = useMemo(() => projectsResponse?.data || [], [projectsResponse]);

  // Clients for the selector (only used/enabled when there is no project)
  const { data: clientsResponse } = useQuery({
    queryKey: ['rfq-builder-clients'],
    queryFn: () => listClients({ pageSize: 500 }),
    staleTime: 1000 * 60 * 5,
  });
  const clients = clientsResponse?.data || [];

  // Existing RFQ (edit mode)
  const {
    data: rfqResponse,
    isLoading: loadingRfq,
    error: loadError,
  } = useQuery({
    queryKey: ['rfq', id],
    queryFn: () => getRfq(id),
    enabled: isEdit,
  });

  const existingRfq = rfqResponse?.data;

  // Hydrate state when loading existing RFQ
  useEffect(() => {
    if (!isEdit || !existingRfq) return;
    setHeader({
      project_id: existingRfq.project_id || '',
      client_id: existingRfq.client_id || '',
      title: existingRfq.title || '',
      notes: existingRfq.notes || '',
    });
    const items = existingRfq.items || [];
    if (items.length === 0) {
      setLines([blankLine()]);
    } else {
      setLines(
        items.map((it) => ({
          __key: it.id,
          alcance: it.alcance || '',
          name: it.name || '',
          description: it.description || '',
          quantity: it.quantity ?? '',
          unit: it.unit || '',
          is_inferred: !!it.is_inferred,
          note: it.note || '',
        }))
      );
    }
  }, [existingRfq, isEdit]);

  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineChange = (index, partial) => {
    setLines((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...partial };
      return next;
    });
  };

  const handleAddLine = () => setLines((prev) => [...prev, blankLine()]);

  const handleRemoveLine = (index) => {
    setLines((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [blankLine()];
    });
  };

  const handleMove = (index, dir) => {
    setLines((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  // The project's own client (shown read-only when a project is selected —
  // #422: with a project, the client is projects.client_id, not rfqs.client_id).
  const selectedProject = useMemo(
    () => projects.find((p) => p.id === header.project_id) || null,
    [projects, header.project_id]
  );
  const hasProject = !!header.project_id;

  // Visual grouping preview by alcance (first-seen order)
  const alcanceOrder = useMemo(() => {
    const seen = [];
    lines.forEach((l) => {
      const key = l.alcance?.trim() || 'Sin alcance';
      if (!seen.includes(key)) seen.push(key);
    });
    return seen;
  }, [lines]);

  // #422: el proyecto ya no es obligatorio (compra por excepción). Sin
  // proyecto, se pide como mínimo Título O Cliente para poder identificar
  // la compra — no se fuerza a completar ambos.
  const validate = () => {
    if (!hasProject && !header.title.trim() && !header.client_id) {
      return 'Sin proyecto, indica al menos un Título o un Cliente para poder identificar la compra';
    }
    const validLines = lines.filter((l) => l.name.trim());
    if (validLines.length === 0) {
      return 'Agrega al menos un ítem con nombre del producto';
    }
    return null;
  };

  // Build the items payload. Captura textual: name/description kept verbatim.
  // A line without a quantity is flagged is_inferred (unless the user
  // explicitly toggled it) so it is never silently dropped.
  const buildItemsPayload = () =>
    lines
      .filter((l) => l.name.trim())
      .map((l, i) => {
        const quantity =
          l.quantity === '' || l.quantity === null ? null : Number(l.quantity);
        return {
          alcance: l.alcance.trim() || null,
          sort_order: i,
          name: l.name,
          description: l.description.trim() || null,
          quantity,
          unit: l.unit.trim() || null,
          is_inferred: l.is_inferred || quantity == null,
          note: l.note.trim() || null,
        };
      });

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const items = buildItemsPayload();
      // client_id sólo aplica sin proyecto (con proyecto, el cliente es
      // projects.client_id — no se duplica en rfqs.client_id).
      const clientId = hasProject ? null : header.client_id || null;
      if (!isEdit) {
        const { data: created, error: createErr } = await createRfq({
          project_id: header.project_id || null,
          client_id: clientId,
          title: header.title.trim() || null,
          notes: header.notes.trim() || null,
          items,
        });
        if (createErr || !created) {
          throw new Error(createErr?.message || 'No se pudo crear la solicitud');
        }
        toast.success('Solicitud creada');
        navigate(`/solicitudes-cotizacion/${created.id}`);
      } else {
        const { error: updErr } = await updateRfq(id, {
          project_id: header.project_id || null,
          client_id: clientId,
          title: header.title.trim() || null,
          notes: header.notes.trim() || null,
        });
        if (updErr) {
          throw new Error(updErr.message || 'No se pudo actualizar la solicitud');
        }
        const { error: itemsErr } = await replaceRfqItems(id, items);
        if (itemsErr) {
          throw new Error(itemsErr.message || 'No se pudieron guardar los ítems');
        }
        toast.success('Solicitud actualizada');
        navigate(`/solicitudes-cotizacion/${id}`);
      }
    } catch (err) {
      console.error('Submit RFQ error:', err);
      setError(err.message || 'Error al guardar la solicitud');
      toast.error('Error al guardar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  if (isEdit && loadingRfq) {
    return (
      <Container fluid className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Cargando solicitud...</p>
      </Container>
    );
  }

  if (isEdit && (loadError || !existingRfq)) {
    return (
      <Container fluid>
        <Alert variant="danger">
          {loadError?.message || 'No se pudo cargar la solicitud'}
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/solicitudes-cotizacion')}>
          <i className="bi bi-arrow-left me-2"></i>
          Volver
        </Button>
      </Container>
    );
  }

  return (
    <DashboardLayout>
    <Container fluid className="rfq-builder">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2 text-decoration-none"
            onClick={() => navigate('/solicitudes-cotizacion')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Volver a solicitudes
          </Button>
          <h2 className="mb-1">
            <i className="bi bi-file-earmark-ruled me-2"></i>
            {isEdit ? 'Editar solicitud' : 'Nueva solicitud de cotización'}
          </h2>
          <p className="text-muted mb-0">
            Se captura el texto tal cual. Los ítems a los que no les pusiste
            cantidad quedan marcados como "falta confirmar".
          </p>
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
        <fieldset disabled={submitting}>
          {/* Header */}
          <Card className="shadow-sm mb-4">
            <Card.Header>
              <strong>Datos de la solicitud</strong>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Group controlId="rfq-project">
                    <Form.Label>Proyecto</Form.Label>
                    <Form.Select
                      value={header.project_id}
                      onChange={(e) => handleHeaderChange('project_id', e.target.value)}
                    >
                      <option value="">Sin proyecto (compra por excepción)</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.project_number ? `${p.project_number} — ` : ''}
                          {p.title || '(Sin título)'}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text muted>
                      Dejalo así para una compra suelta que no nace de un proyecto.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="rfq-client">
                    <Form.Label>Cliente</Form.Label>
                    {hasProject ? (
                      <Form.Control
                        type="text"
                        value={selectedProject?.client?.company_name || 'Sin cliente en el proyecto'}
                        disabled
                        readOnly
                      />
                    ) : (
                      <Form.Select
                        value={header.client_id}
                        onChange={(e) => handleHeaderChange('client_id', e.target.value)}
                      >
                        <option value="">Sin cliente</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.company_name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                    <Form.Text muted>
                      {hasProject
                        ? 'Se toma del proyecto seleccionado.'
                        : 'Sin proyecto, indica el cliente si corresponde (opcional si ya pusiste un Título).'}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label>Título</Form.Label>
                  <Form.Control
                    type="text"
                    value={header.title}
                    onChange={(e) => handleHeaderChange('title', e.target.value)}
                    placeholder="Ej. Materiales ductos sala A (opcional)"
                    maxLength={200}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Label>Notas</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={header.notes}
                    onChange={(e) => handleHeaderChange('notes', e.target.value)}
                    placeholder="Observaciones para la ferretería (opcional)"
                    maxLength={1000}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Line items */}
          <Card className="shadow-sm mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Ítems</strong>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddLine}
                disabled={submitting}
              >
                <i className="bi bi-plus-lg me-1"></i>
                Agregar ítem
              </Button>
            </Card.Header>
            <Card.Body>
              {alcanceOrder.length > 0 && (
                <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                  <span className="small text-muted">Alcances:</span>
                  {alcanceOrder.map((a) => (
                    <Badge key={a} bg="light" text="dark" className="border">
                      {a}
                    </Badge>
                  ))}
                </div>
              )}

              {lines.map((line, index) => (
                <Card key={line.__key} className="mb-3 border rfq-item-row">
                  <Card.Body className="py-3">
                    <Row className="g-2 align-items-end">
                      <Col xs={12} md={4}>
                        <Form.Label className="small text-muted mb-1">Alcance</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={line.alcance}
                          onChange={(e) => handleLineChange(index, { alcance: e.target.value })}
                          placeholder="Ej. Alcance 1 (opcional)"
                          maxLength={200}
                        />
                      </Col>
                      <Col xs={6} md={2}>
                        <Form.Label className="small text-muted mb-1">Cantidad</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          min="0"
                          step="any"
                          value={line.quantity}
                          onChange={(e) => handleLineChange(index, { quantity: e.target.value })}
                          placeholder="—"
                        />
                      </Col>
                      <Col xs={6} md={2}>
                        <Form.Label className="small text-muted mb-1">Unidad</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={line.unit}
                          onChange={(e) => handleLineChange(index, { unit: e.target.value })}
                          placeholder="pza, m, kg..."
                          maxLength={40}
                        />
                      </Col>
                      <Col xs={12} md={4} className="text-md-end">
                        <div className="d-flex justify-content-md-end align-items-center gap-1 mt-2 mt-md-0">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleMove(index, -1)}
                            disabled={index === 0}
                            title="Subir"
                            aria-label="Subir ítem"
                          >
                            <i className="bi bi-arrow-up"></i>
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleMove(index, 1)}
                            disabled={index === lines.length - 1}
                            title="Bajar"
                            aria-label="Bajar ítem"
                          >
                            <i className="bi bi-arrow-down"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveLine(index)}
                            title="Quitar"
                            aria-label="Quitar ítem"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <Form.Label className="small text-muted mb-1">
                          Nombre del producto <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={line.name}
                          onChange={(e) => handleLineChange(index, { name: e.target.value })}
                          placeholder="Texto tal cual se dictó (no se corrige la jerga)"
                          maxLength={300}
                        />
                      </Col>
                      <Col xs={12}>
                        <Form.Label className="small text-muted mb-1">
                          Descripción / especificación
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={line.description}
                          onChange={(e) => handleLineChange(index, { description: e.target.value })}
                          placeholder="Detalle o especificación (opcional)"
                          maxLength={500}
                        />
                      </Col>
                      <Col xs={12} md={8}>
                        <Form.Label className="small text-muted mb-1">Nota / interpretación</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={line.note}
                          onChange={(e) => handleLineChange(index, { note: e.target.value })}
                          placeholder="Qué se entendió, a confirmar, etc. (opcional)"
                          maxLength={500}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        {/* "Inferido" es el nombre de la columna (`is_inferred`)
                            leido en voz alta: fuera del esquema nadie sabe que
                            significa. Lo que el usuario necesita saber es que
                            ese renglon queda pendiente de confirmar con el
                            proveedor. */}
                        <Form.Check
                          type="switch"
                          id={`inferred-${line.__key}`}
                          className="mt-2"
                          label="Falta confirmar la cantidad"
                          checked={line.is_inferred}
                          onChange={(e) => handleLineChange(index, { is_inferred: e.target.checked })}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </Card.Body>
          </Card>

          {error && (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {/* Actions */}
          <div className="d-flex flex-column flex-md-row justify-content-end gap-2">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => navigate('/solicitudes-cotizacion')}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-2"></i>
                  {isEdit ? 'Guardar cambios' : 'Crear solicitud'}
                </>
              )}
            </Button>
          </div>
        </fieldset>
      </Form>
    </Container>
    </DashboardLayout>
  );
};

export default RfqBuilder;
